/**
 * Reconciliation: which real transaction fulfils which planned occurrence.
 *
 * Nothing is stored. A plan is a category on a schedule, so a transaction in
 * that category, pointing the right way, belongs to whichever occurrence of the
 * plan it lands nearest — and that is worked out fresh every time rather than
 * written down and kept in step. Re-file a transaction and the plan it answers
 * to changes with it: there is no link to correct, and none to go stale.
 *
 * The amount never decides whether a transaction belongs. A bill that came in
 * double is still that bill, and saying so is the whole point of having planned
 * it: that is what *over plan* means, and the tolerance says how far is far.
 */
import { diffDays } from './dates'
import { round2 } from './money'
import { covers, nearestSlot, nextOccurrence, occurrences, stepBy } from './recurrence'

const signMatches = (tx, plan) => (plan.type === 'income' ? tx.amount > 0 : tx.amount < 0)

/** The whole of it: this transaction is one of the ones this plan expects. */
export const belongsTo = (tx, plan) =>
  !!plan &&
  !plan.archived &&
  !!plan.category &&
  tx.category === plan.category &&
  signMatches(tx, plan)

/**
 * The occurrence of `plan` that `tx` falls in, or null when it belongs to none.
 *
 * The slot is the one nearest the transaction's date — the plan's own rhythm
 * decides where one occurrence ends and the next begins — and it has to be a
 * date the plan was actually running on, so a stray row years after a plan
 * finished is not hung on its final occurrence.
 */
export function slotFor(plan, tx) {
  if (!belongsTo(tx, plan)) return null
  const slot = nearestSlot(plan, tx.date)
  return slot && covers(plan, slot) ? slot : null
}

/**
 * Which plan occurrence a transaction fulfils, across all of them:
 * `{ plan, due }`, or null. Plans sharing a category are separated by which one
 * the transaction lands nearest, then by id so the answer never wobbles.
 */
export function fulfilment(plans, tx) {
  let best = null
  for (const plan of plans) {
    const due = slotFor(plan, tx)
    if (!due) continue
    const gap = Math.abs(diffDays(due, tx.date))
    if (!best || gap < best.gap || (gap === best.gap && plan.id < best.plan.id)) {
      best = { plan, due, gap }
    }
  }
  return best && { plan: best.plan, due: best.due }
}

/* ------------------------------------------------------------ automatic plans */

/** How many earlier occurrences an automatic plan averages, when it does not say. */
export const AUTO_PERIODS_DEFAULT = 3

export const autoPeriodsOf = (plan) => {
  const n = Math.round(Number(plan?.auto_periods))
  return Number.isFinite(n) && n >= 1 ? Math.min(n, 120) : AUTO_PERIODS_DEFAULT
}

/** A one-off has nothing before it to learn from, so it is never automatic. */
export const isAuto = (plan) => !!plan?.auto && plan.cadence !== 'once'

/**
 * What each automatic plan's past occurrences came to: planId -> (slot -> sum).
 *
 * The same split as the schedule — a transaction goes to the rival plan whose
 * slot it lands nearest — except that a plan's own dates do not fence it in.
 * History from before a plan started is still what that bill has cost; the
 * plan is new, the bill is not.
 */
export function autoHistory(plans, transactions) {
  const auto = plans.filter((plan) => !plan.archived && isAuto(plan) && plan.category)
  const out = new Map(auto.map((plan) => [plan.id, new Map()]))
  if (!auto.length) return out
  const rivals = new Map()
  for (const plan of plans) {
    if (plan.archived || !plan.category) continue
    if (!rivals.has(plan.category)) rivals.set(plan.category, [])
    rivals.get(plan.category).push(plan)
  }
  for (const tx of transactions) {
    const candidates = tx.category ? rivals.get(tx.category) : null
    if (!candidates) continue
    let best = null
    for (const plan of candidates) {
      if (!signMatches(tx, plan)) continue
      const slot = nearestSlot(plan, tx.date)
      if (!slot) continue
      const gap = Math.abs(diffDays(slot, tx.date))
      if (!best || gap < best.gap || (gap === best.gap && plan.id < best.plan.id)) {
        best = { plan, slot, gap }
      }
    }
    const slots = best && out.get(best.plan.id)
    if (slots) slots.set(best.slot, (slots.get(best.slot) ?? 0) + Math.abs(tx.amount))
  }
  return out
}

/**
 * What an automatic plan expects of the occurrence due on `due`: the average
 * of what came in for the N occurrences before it — `{ amount, basis }`, basis
 * being how many of those actually had something to average.
 *
 * Unlike a budget, an occurrence where nothing came is left out rather than
 * counted as nothing. A budget measures spending, and a month with none really
 * did cost nothing; a plan predicts the size of *one payment*, and a bill that
 * is late or skipped says nothing about how big it is. It is already reported
 * as missing — counting it as zero too would drag the next figure down and
 * then call the real bill over plan.
 */
export function autoPlanFigure(plan, due, slots) {
  if (!slots) return { amount: 0, basis: 0 }
  let total = 0
  let basis = 0
  for (let back = 1; back <= autoPeriodsOf(plan); back++) {
    // Stepped from `due` each time rather than chained, so a 31st clamped into
    // February does not drag every earlier month to the 28th.
    const paid = slots.get(nearestSlot(plan, stepBy(plan.cadence, due, -back)))
    if (!paid) continue
    basis++
    total += paid
  }
  return { amount: basis ? round2(total / basis) : 0, basis }
}

/**
 * The plans with each automatic one's `amount` set to what it expects next —
 * for everything that reads a plan's figure outside the schedule (next month's
 * totals, the sheet's preview). The schedule itself works per occurrence.
 */
export function withAutoAmounts(plans, transactions, today) {
  if (!plans.some(isAuto)) return plans
  const history = autoHistory(plans, transactions)
  return plans.map((plan) => {
    if (!isAuto(plan)) return plan
    // The next one due; a plan that has finished keeps what it last expected.
    const due = nextOccurrence(plan, today) ?? nearestSlot(plan, today)
    const figure = autoPlanFigure(plan, due, history.get(plan.id))
    return { ...plan, amount: figure.amount, autoBasis: figure.basis }
  })
}

/** How far a transaction's amount sits from the plan's, as a share of it. */
export function amountDrift(tx, plan) {
  const expected = Math.abs(plan.amount)
  if (expected <= 0) return Infinity
  return Math.abs(Math.abs(tx.amount) - expected) / expected
}

/** Tone only: the words live in the catalogues, under `status.<key>`. */
export const STATUS = {
  matched: { tone: 'good' },
  over: { tone: 'warn' },
  under: { tone: 'warn' },
  missing: { tone: 'danger' },
  due: { tone: 'info' },
  upcoming: { tone: 'muted' },
}

/**
 * The plan-versus-reality view for a window: one row per expected occurrence,
 * carrying the transactions that fulfil it and how far off they are.
 *
 * A category may collect several transactions in one period — a grocery plan is
 * eight shops, not one — so an occurrence holds all of them and `actual` is
 * their sum. That is the figure the plan was a guess at.
 */
export function buildSchedule(plans, transactions, { from, to, today }) {
  const live = plans.filter((plan) => !plan.archived)

  // By category first, so a transaction is only measured against the plans that
  // could possibly want it rather than against every plan there is.
  const wanted = new Map()
  for (const plan of live) {
    if (!plan.category) continue
    if (!wanted.has(plan.category)) wanted.set(plan.category, [])
    wanted.get(plan.category).push(plan)
  }

  const bySlot = new Map()
  for (const tx of transactions) {
    const rivals = tx.category ? wanted.get(tx.category) : null
    if (!rivals) continue
    const hit = fulfilment(rivals, tx)
    if (!hit) continue
    const key = `${hit.plan.id}:${hit.due}`
    if (!bySlot.has(key)) bySlot.set(key, [])
    bySlot.get(key).push(tx)
  }

  const history = autoHistory(live, transactions)

  const rows = []
  for (const plan of live) {
    for (const due of occurrences(plan, from, to)) {
      const matches = bySlot.get(`${plan.id}:${due}`) ?? []
      const actual = matches.reduce((sum, t) => sum + Math.abs(t.amount), 0)
      // An automatic plan expects, each time, what the times before it came to.
      const auto = isAuto(plan) ? autoPlanFigure(plan, due, history.get(plan.id)) : null
      const expected = auto ? auto.amount : Math.abs(plan.amount)
      const tolerance = Math.max(0.01, plan.tolerance ?? 0.1)
      const variance = actual - expected

      let status
      if (matches.length) {
        const drift = expected > 0 ? Math.abs(variance) / expected : 0
        status = drift <= tolerance ? 'matched' : variance > 0 ? 'over' : 'under'
      } else if (due < today) {
        status = 'missing'
      } else {
        status = diffDays(today, due) <= 5 ? 'due' : 'upcoming'
      }

      rows.push({
        id: `${plan.id}:${due}`,
        plan,
        planId: plan.id,
        type: plan.type,
        due,
        expected,
        // How many earlier payments `expected` averages, on an automatic plan;
        // 0 means none came and there is no figure. Null on a fixed plan.
        autoBasis: auto ? auto.basis : null,
        actual,
        variance,
        matches,
        status,
        overdue: status === 'missing',
      })
    }
  }
  return rows.sort(
    (a, b) => a.due.localeCompare(b.due) || a.plan.category.localeCompare(b.plan.category),
  )
}
